import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { messagesApi } from '@/lib/api/messages';

export function useInbox(params?: {
  page?: number;
  limit?: number;
  type?: string;
  unreadOnly?: boolean;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['messages', 'inbox', params],
    queryFn: () => messagesApi.getInbox(params),
  });

  return {
    inbox: data?.data || [],
    meta: data?.meta,
    isLoading,
    error,
  };
}

export function useUnreadCount() {
  const { data, isLoading } = useQuery({
    queryKey: ['messages', 'unread-count'],
    queryFn: () => messagesApi.getUnreadCount(),
    refetchInterval: 30000,
  });

  return {
    unreadCount: data?.total || 0,
    unreadAnnouncements: data?.announcements || 0,
    unreadDirect: data?.direct || 0,
    isLoading,
  };
}

export function useConversationDetail(conversationId: string) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['messages', 'conversation', conversationId],
    queryFn: () => messagesApi.getConversationDetail(conversationId),
    enabled: !!conversationId,
  });

  return { conversation: data, isLoading, error };
}

export function useMessageMutations() {
  const queryClient = useQueryClient();

  const markAsRead = useMutation({
    mutationFn: (conversationId: string) =>
      messagesApi.markAsRead(conversationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
  });

  const reply = useMutation({
    mutationFn: ({
      conversationId,
      content,
    }: {
      conversationId: string;
      content: Record<string, unknown>;
    }) => messagesApi.reply(conversationId, content),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['messages', 'conversation', variables.conversationId],
      });
      queryClient.invalidateQueries({ queryKey: ['messages', 'inbox'] });
    },
  });

  return {
    markAsRead: markAsRead.mutateAsync,
    reply: reply.mutateAsync,
    isMarkingRead: markAsRead.isPending,
    isReplying: reply.isPending,
  };
}

export function useAdminMessages() {
  const queryClient = useQueryClient();

  const useAnnouncements = (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }) => {
    const { data, isLoading } = useQuery({
      queryKey: ['messages', 'admin', 'announcements', params],
      queryFn: () => messagesApi.listAnnouncements(params),
    });
    return { announcements: data?.data || [], meta: data?.meta, isLoading };
  };

  const useDirectMessages = (params?: { page?: number; limit?: number }) => {
    const { data, isLoading } = useQuery({
      queryKey: ['messages', 'admin', 'direct', params],
      queryFn: () => messagesApi.listDirectMessages(params),
    });
    return { directMessages: data?.data || [], meta: data?.meta, isLoading };
  };

  const createAnnouncement = useMutation({
    mutationFn: messagesApi.createAnnouncement,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['messages', 'admin', 'announcements'],
      });
    },
  });

  const publishAnnouncement = useMutation({
    mutationFn: messagesApi.publishAnnouncement,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['messages', 'admin', 'announcements'],
      });
    },
  });

  const sendDirectMessage = useMutation({
    mutationFn: messagesApi.sendDirectMessage,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['messages', 'admin', 'direct'],
      });
    },
  });

  const deleteAnnouncement = useMutation({
    mutationFn: messagesApi.deleteAnnouncement,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['messages', 'admin', 'announcements'],
      });
    },
  });

  return {
    useAnnouncements,
    useDirectMessages,
    createAnnouncement: createAnnouncement.mutateAsync,
    publishAnnouncement: publishAnnouncement.mutateAsync,
    sendDirectMessage: sendDirectMessage.mutateAsync,
    deleteAnnouncement: deleteAnnouncement.mutateAsync,
    isCreating: createAnnouncement.isPending,
    isPublishing: publishAnnouncement.isPending,
    isSending: sendDirectMessage.isPending,
    isDeleting: deleteAnnouncement.isPending,
  };
}
